import React from "react";
import { ExternalLinkIcon } from "@heroicons/react/solid";
import { projects } from "../data";

// Design tokens for this section — keep in sync if you extend the palette
// bg        #0B0D12   surface   #12151B   border   #232733
// text      #EDEFF3   muted     #8D93A3
// accent-1  #9C8CFF (violet)    accent-2  #F0B858 (amber)

export default function Projects() {
  return (
    <section id="projects" className="bg-[#0B0D12] text-[#8D93A3]">
      <div className="container mx-auto px-6 py-24 lg:px-8 max-w-6xl">
        {/* Section header, styled like an editor file tab */}
        <div className="mb-16 max-w-2xl">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-md border border-[#232733] bg-[#12151B]">
            <span className="w-2 h-2 rounded-full bg-[#F0B858]" />
            <span className="font-mono text-xs tracking-wide text-[#8D93A3]">
              projects.tsx
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#EDEFF3]">
            Apps I&apos;ve built
            <span className="inline-block w-[10px] h-[1.9rem] bg-[#9C8CFF] ml-2 align-middle animate-[blink_1.1s_steps(1)_infinite]" />
          </h2>
          <p className="mt-4 text-base leading-relaxed text-[#8D93A3]">
            A working set of interfaces built with React — some are polished
            products, others are quick experiments. Each one shipped end to
            end, from state management to the pixel.
          </p>
        </div>

        {/* Project grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {projects.map((project, index) => (
            <a
              key={project.title ?? index}
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group relative block rounded-xl overflow-hidden border border-[#232733] bg-[#12151B] transition-colors duration-300 hover:border-[#9C8CFF]/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#9C8CFF]/60"
            >
              {/* Image */}
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={project.image}
                  alt={project.title}
                  loading="lazy"
                  className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0B0D12] via-[#0B0D12]/70 to-transparent" />

                {/* Index, top-left, matches the mono/line-number motif */}
                <span className="absolute top-4 left-4 font-mono text-xs text-[#EDEFF3]/70">
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>

              {/* Content overlay */}
              <div className="absolute inset-x-0 bottom-0 p-6">
                {project.subtitle && (
                  <p className="font-mono text-xs tracking-wide text-[#F0B858] mb-1.5">
                    {project.subtitle}
                  </p>
                )}

                <h3 className="text-lg font-medium text-[#EDEFF3]">
                  {project.title}
                </h3>

                {/* Description + link: hidden height until hover, no opacity-0 flash */}
                <div className="grid grid-rows-[0fr] group-hover:grid-rows-[1fr] transition-[grid-template-rows] duration-300 ease-out">
                  <div className="overflow-hidden">
                    <p className="pt-2 text-sm leading-relaxed text-[#8D93A3]">
                      {project.description}
                    </p>

                    {Array.isArray(project.tags) && project.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 pt-3">
                        {project.tags.map((tag) => (
                          <span
                            key={tag}
                            className="font-mono text-[11px] px-2 py-0.5 rounded border border-[#232733] bg-[#0B0D12] text-[#8D93A3]"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <span className="inline-flex items-center gap-1 pt-3 text-sm font-medium text-[#9C8CFF]">
                      View project
                      <ExternalLinkIcon className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                  </div>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes blink { 50% { opacity: 0; } }
      `}</style>
    </section>
  );
}